import React, { createContext, useState, useContext, useEffect } from "react";
import Cookies from "js-cookie";
import { clearStaffCookies } from "@shared/lib/staffSession";
import { useQueryClient } from "@tanstack/react-query";

import { getMe } from "../api/me";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // AuthProvider mounts inside QueryClientProvider (src/App.js) — the
  // React-Query cache is cleared on login/logout so one principal's
  // scoped rows (exhibitions, zones, stats) never flash for the next.
  const queryClient = useQueryClient();
  const [token, setToken] = useState(() => Cookies.get("staff_token"));
  const [username, setUsername] = useState(() => Cookies.get("staff_username"));
  // F-A: `role` gates the admin-only Users/Org nav. Sourced from the login
  // response (TokenResponse.role), persisted alongside the token cookie.
  const [role, setRole] = useState(() => Cookies.get("staff_role"));
  // Actor's own scope (TokenResponse.teamIds / departmentId, camelCase
  // wire): the exhibition form filters its owner selects to these so a
  // non-admin is only offered owners the scope-lockout guard will accept.
  const [teamIds, setTeamIds] = useState(() => {
    try {
      return JSON.parse(Cookies.get("staff_team_ids") || "[]");
    } catch {
      return [];
    }
  });
  const [departmentId, setDepartmentId] = useState(
    () => Cookies.get("staff_department_id") || null,
  );

  // Cookie values are login-time snapshots; refresh role + scope from
  // /staff/me on load so a team/department (or role) changed mid-session
  // reaches the UI without re-login. A 401 means the token is dead
  // (expired, or the user was disabled/deleted) — drop the session.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getMe()
      .then((me) => {
        if (cancelled) return;
        setRole(me.role);
        setTeamIds(me.teamIds ?? []);
        setDepartmentId(me.departmentId ?? null);
        if (me.role) Cookies.set("staff_role", me.role, { expires: 1 / 12 });
        Cookies.set("staff_team_ids", JSON.stringify(me.teamIds ?? []), {
          expires: 1 / 12,
        });
        if (me.departmentId) {
          Cookies.set("staff_department_id", me.departmentId, {
            expires: 1 / 12,
          });
        } else {
          Cookies.remove("staff_department_id");
        }
      })
      .catch((err) => {
        if (!cancelled && err?.status === 401) logout();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = (newToken, newUsername, newRole, scope) => {
    queryClient.clear();
    Cookies.set("staff_token", newToken, { expires: 1 / 12 }); // 2 hours
    Cookies.set("staff_username", newUsername, { expires: 1 / 12 });
    if (newRole) Cookies.set("staff_role", newRole, { expires: 1 / 12 });
    const ids = scope?.teamIds ?? [];
    const dep = scope?.departmentId ?? null;
    Cookies.set("staff_team_ids", JSON.stringify(ids), { expires: 1 / 12 });
    if (dep) {
      Cookies.set("staff_department_id", dep, { expires: 1 / 12 });
    } else {
      Cookies.remove("staff_department_id");
    }
    setToken(newToken);
    setUsername(newUsername);
    setRole(newRole ?? null);
    setTeamIds(ids);
    setDepartmentId(dep);
  };

  const logout = () => {
    queryClient.clear();
    clearStaffCookies();
    setToken(null);
    setUsername(null);
    setRole(null);
    setTeamIds([]);
    setDepartmentId(null);
  };

  const isAuthenticated = !!token;
  const isAdmin = role === "admin";

  return (
    <AuthContext.Provider
      value={{
        token,
        username,
        role,
        isAdmin,
        teamIds,
        departmentId,
        login,
        logout,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
